import React, { useRef } from 'react';
import { Field, Form } from 'react-final-form';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
} from '@cherre-frontend/ui';
import { DbSysPropertyTypesIds } from 'src/const';
import {
  CustomPropertyAttribute,
  CustomPropertyAttributeType,
} from '../../types/CustomPropertyAttribute';
import { createSlugFromLabel } from './utils/createSlugFromLabel';

interface CustomAttributeFormProps {
  onSave: (attribute: CustomPropertyAttribute) => void;
  onCancel: () => void;
  attributeToEdit?: CustomPropertyAttribute;
  allAttributes: CustomPropertyAttribute[];
}

const { INVESTMENT, PROVIDER } = DbSysPropertyTypesIds;

const attributeTypeOptions = [
  { label: 'String', value: CustomPropertyAttributeType.STRING },
  { label: 'Date (YYYY-MM-DD)', value: CustomPropertyAttributeType.DATE },
  { label: 'Integer', value: CustomPropertyAttributeType.INTEGER },
  { label: 'Number', value: CustomPropertyAttributeType.NUMBER },
];

const propertyTypeOptions = [
  { label: 'Target', value: INVESTMENT },
  { label: 'Source', value: PROVIDER },
];

const emptyAttribute: CustomPropertyAttribute = {
  order: 0,
  slug: '',
  label: '',
  active: true,
  required: false,
  type: CustomPropertyAttributeType.STRING,
  isTargetPropertyType: true,
  propertyTypeId: INVESTMENT,
};

export const CustomAttributeForm = ({
  onSave,
  onCancel,
  allAttributes,
  attributeToEdit,
}: CustomAttributeFormProps) => {
  const initialValues = useRef(attributeToEdit || emptyAttribute);

  const handleSubmit = (values: CustomPropertyAttribute) => {
    const slug = attributeToEdit
      ? attributeToEdit.slug
      : createSlugFromLabel(values.label, allAttributes);
    onSave({
      ...values,
      slug,
      label: values.label.trim(),
      isTargetPropertyType: values.propertyTypeId === INVESTMENT,
    });
  };

  const attributeNameValidator = (value: string | undefined) => {
    if (!value || !value.trim()) {
      return 'Please define the attribute name';
    }

    const filteredAttributes = attributeToEdit
      ? allAttributes.filter(({ slug }) => slug !== attributeToEdit.slug)
      : allAttributes;
    return filteredAttributes.some(({ label }) => label.trim() === value.trim())
      ? 'Attribute name already exists. Please input a different name'
      : undefined;
  };

  return (
    <Form onSubmit={handleSubmit} initialValues={initialValues.current}>
      {({ handleSubmit, invalid }) => (
        <Box
          component='form'
          onSubmit={handleSubmit}
          display='grid'
          rowGap={1}
          columnGap='20px'
          gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }}
        >
          <Field name='label' validate={attributeNameValidator}>
            {({ input, meta: { error, touched } }) => (
              <TextField
                size='small'
                required
                autoFocus
                margin='dense'
                variant='outlined'
                label='Attribute Name'
                error={touched && !!error}
                helperText={touched && error && input.value ? error : undefined}
                {...input}
                FormHelperTextProps={{
                  sx: { fontFamily: 'Montserrat', fontWeight: 600 },
                }}
              />
            )}
          </Field>

          <Field name='type'>
            {({ input }) => (
              <TextField
                select
                size='small'
                margin='dense'
                variant='outlined'
                label='Data type'
                {...input}
              >
                {attributeTypeOptions.map(({ label, value }) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </TextField>
            )}
          </Field>

          <Field name='propertyTypeId'>
            {({ input }) => (
              <TextField
                select
                size='small'
                margin='dense'
                variant='outlined'
                label='Source or Target'
                {...input}
              >
                {propertyTypeOptions.map(({ label, value }) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </TextField>
            )}
          </Field>

          <Stack direction='row' alignItems='center' gap={1}>
            <Field name='required' type='checkbox'>
              {({ input }) => (
                <FormControlLabel
                  label='Required?'
                  sx={{ flexGrow: 1 }}
                  control={<Checkbox color='primary' {...input} />}
                />
              )}
            </Field>

            <Stack direction='row' alignItems='center' gap={1}>
              <Button
                color='primary'
                variant='text'
                size='small'
                onClick={onCancel}
              >
                Cancel
              </Button>
              <Button
                color='primary'
                variant='contained'
                size='small'
                type='submit'
                disabled={invalid}
              >
                Save
              </Button>
            </Stack>
          </Stack>
        </Box>
      )}
    </Form>
  );
};
